import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAdminDashboardState } from './useAdminDashboardState';
import { useAdminDashboardHandlers } from './useAdminDashboardHandlers';
import { filterUsers, getPaginatedUsers } from './utils';
import { AdminDashboardHeader } from './AdminDashboardHeader';
import { StatsCards } from './StatsCards';
import { UserSearchBar } from './UserSearchBar';
import { UserForm } from './UserForm';
import { UserTable } from './UserTable';
import { Pagination } from './Pagination';
import { DeleteUserModal } from './DeleteUserModal';
import { ToggleAdminModal } from './ToggleAdminModal';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: currentUser,
    isAuthenticated,
    isAdmin,
    loading: authLoading,
    logout,
    startMimicSession,
  } = useAuth();

  const state = useAdminDashboardState();
  const handlers = useAdminDashboardHandlers({
    state,
    currentUser,
    startMimicSession,
  });

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/dashboard');
      return;
    }

    if (isAuthenticated && isAdmin) {
      handlers.loadUsers();
      handlers.loadSubscriptionStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isAuthenticated, isAdmin, authLoading]);

  // Handle edit user from UserDetails page
  useEffect(() => {
    if (location.state?.editUserId && state.users.length > 0) {
      const userToEdit = state.users.find(u => u.id === location.state.editUserId);
      if (userToEdit) {
        handlers.startEdit(userToEdit);
        navigate(location.pathname, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, state.users]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    state.setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchTerm, state.filterRole]);

  const filteredUsers = filterUsers(state.users, state.searchTerm, state.filterRole);
  const totalPages = Math.ceil(filteredUsers.length / state.usersPerPage);
  const { currentUsers, startIndex, endIndex } = getPaginatedUsers(
    filteredUsers,
    state.currentPage,
    state.usersPerPage
  );

  if (authLoading || state.isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">
            {authLoading ? 'Authenticating...' : 'Loading admin dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="page-container">
      <AdminDashboardHeader onLogout={logout} />

      <main className="page-content max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <StatsCards users={state.users} subscriptionStats={state.subscriptionStats} />

          <div className="card">
            <UserSearchBar
              searchTerm={state.searchTerm}
              filterRole={state.filterRole}
              totalUsers={state.users.length}
              totalFiltered={filteredUsers.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onSearchChange={state.setSearchTerm}
              onFilterChange={state.setFilterRole}
              onAddUser={() => state.setShowCreateForm(true)}
            />

            {state.showCreateForm && (
              <UserForm
                editingUser={state.editingUser}
                formData={state.formData}
                isSubmitting={state.isSubmitting}
                onFormDataChange={state.setFormData}
                onSubmit={state.editingUser ? handlers.handleUpdateUser : handlers.handleCreateUser}
                onCancel={handlers.resetForm}
              />
            )}

            <UserTable
              users={currentUsers}
              currentUser={currentUser}
              mimicingUserId={state.mimicingUserId}
              searchTerm={state.searchTerm}
              filterRole={state.filterRole}
              onMimicUser={handlers.handleMimicUser}
              onBuildRoaryForUser={handlers.handleBuildRoaryForUser}
              onToggleAdmin={handlers.startToggleAdmin}
              onEditUser={handlers.startEdit}
              onDeleteUser={handlers.startDelete}
            />

            <Pagination
              currentPage={state.currentPage}
              totalPages={totalPages}
              onPageChange={handlers.handlePageChange}
              onPreviousPage={handlers.handlePreviousPage}
              onNextPage={() => handlers.handleNextPage(totalPages)}
            />
          </div>
        </div>
      </main>

      {state.showDeleteModal && state.userToDelete && (
        <DeleteUserModal
          user={state.userToDelete}
          isSubmitting={state.isSubmitting}
          onConfirm={handlers.handleDeleteUser}
          onCancel={() => {
            state.setShowDeleteModal(false);
            state.setUserToDelete(null);
          }}
        />
      )}

      {state.showToggleAdminModal && state.userToToggleAdmin && (
        <ToggleAdminModal
          user={state.userToToggleAdmin}
          isSubmitting={state.isSubmitting}
          onConfirm={handlers.handleToggleAdmin}
          onCancel={() => {
            state.setShowToggleAdminModal(false);
            state.setUserToToggleAdmin(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
